import logger from '@/lib/logger'

// Lazy PostHog loader.
//
// `posthog-js` is ~50KB gzipped, so it must never be reachable from a static
// import — that pulls it into the first-load bundle no matter how late `init()`
// is called. It is imported dynamically on first use (warmed during idle time
// from the provider); events fired before the library resolves are queued and
// flushed on load.

type PostHog = (typeof import('posthog-js'))['default']

let instance: PostHog | null = null
let loader: Promise<PostHog | null> | null = null
const queue: Array<(ph: PostHog) => void> = []

export function analyticsDisabled(): boolean {
  return (
    typeof window === 'undefined' ||
    window.location.hostname === 'localhost' ||
    !process.env.NEXT_PUBLIC_POSTHOG_KEY
  )
}

const INTERNAL_PROPERTY = 'internal_device'

/**
 * Visiting any page with `?internal=<label>` (e.g. `?internal=laptop`) tags
 * this browser as one of Arne's devices; `?internal=off` clears it. The tag is
 * a persisted super property, so every later event carries it and the
 * project's internal-user filter drops it. Runs from `loaded`, which fires
 * before the initial `$pageview` is captured, so that pageview is tagged too.
 */
function markInternalDevice(
  ph: Pick<PostHog, 'register' | 'unregister'>,
): void {
  const label = new URLSearchParams(window.location.search).get('internal')
  if (label === null) return
  if (label === 'off') ph.unregister(INTERNAL_PROPERTY)
  else ph.register({ [INTERNAL_PROPERTY]: label || 'true' })
}

/** Download + initialize PostHog. Safe to call repeatedly; resolves once. */
export function loadAnalytics(): Promise<PostHog | null> {
  if (analyticsDisabled()) return Promise.resolve(null)
  if (instance) return Promise.resolve(instance)
  if (!loader) {
    loader = import('posthog-js')
      .then(({ default: posthog }) => {
        posthog.init(process.env.NEXT_PUBLIC_POSTHOG_KEY as string, {
          api_host: '/ingest',
          ui_host: 'https://us.posthog.com',
          person_profiles: 'identified_only',
          // Trim the runtime: we only send explicit events, so skip the
          // autocapture, session-recording, and surveys modules (the last of
          // which loads a separate surveys.js bundle).
          autocapture: false,
          disable_session_recording: true,
          disable_surveys: true,
          // Capture pageleave so PostHog records native scroll depth
          // (`$prev_pageview_max_scroll_percentage`), bounce rate, and accurate
          // session duration — no custom scroll instrumentation required. This
          // rides on the existing `$pageview` capture; it doesn't pull in the
          // autocapture module.
          capture_pageleave: true,
          // Mouse/scroll heatmaps. The clickmap view needs autocapture, which
          // stays off; the heatmap and scrollmap views don't.
          enable_heatmaps: true,
          loaded: markInternalDevice,
        })
        instance = posthog
        for (const fn of queue.splice(0)) fn(posthog)
        return posthog
      })
      .catch((error) => {
        logger.error({ error }, '[Analytics] failed to load PostHog')
        return null
      })
  }
  return loader
}

function withPostHog(fn: (ph: PostHog) => void): void {
  if (analyticsDisabled()) return
  if (instance) {
    fn(instance)
    return
  }
  queue.push(fn)
  void loadAnalytics()
}

export function captureEvent(
  event: string,
  properties?: Record<string, unknown>,
): void {
  withPostHog((ph) => ph.capture(event, properties))
}

export function identifyUser(
  userId: string,
  traits?: Record<string, unknown>,
): void {
  withPostHog((ph) => ph.identify(userId, traits))
}
